import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApplyConfirmDialog } from "./admin-pricing/ApplyConfirmDialog";
import { MarginTab } from "./admin-pricing/MarginTab";
import { PromoTab } from "./admin-pricing/PromoTab";
import { toast } from "sonner";
import { Percent, Tag } from "lucide-react";

export default function AdminPricing() {
  const { authAxios } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [applying, setApplying] = useState(false);
  const [applyingAuth, setApplyingAuth] = useState(false);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAuthDialogOpen, setConfirmAuthDialogOpen] = useState(false);
  const [confirmDiscountDialogOpen, setConfirmDiscountDialogOpen] = useState(false);

  // Edited values per tab (key: catId)
  const [editedMarkups, setEditedMarkups] = useState({});
  const [editedAuthMarkups, setEditedAuthMarkups] = useState({});
  const [editedDiscountMargins, setEditedDiscountMargins] = useState({});

  // Bulk inputs per tab
  const [bulkMargin, setBulkMargin] = useState("");
  const [bulkAuthMargin, setBulkAuthMargin] = useState("");
  const [bulkDiscountMargin, setBulkDiscountMargin] = useState("");

  // Promo state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [promoPrice, setPromoPrice] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [savingPromo, setSavingPromo] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        authAxios.get("/categories"),
        authAxios.get("/products"),
      ]);
      setCategories(categoriesRes.data.filter(c => c.product_count > 0));
      setProducts(productsRes.data);

      const markups = {};
      const authMarkups = {};
      const discountMargins = {};
      categoriesRes.data.forEach(cat => {
        markups[cat.id] = cat.markup_percentage || 15;
        authMarkups[cat.id] = cat.auth_markup_percentage ?? "";
        discountMargins[cat.id] = cat.discount_margin_percentage ?? "";
      });
      setEditedMarkups(markups);
      setEditedAuthMarkups(authMarkups);
      setEditedDiscountMargins(discountMargins);
    } catch {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ----- Generic save helpers -----
  const buildSaveSingle = (endpointFor, fieldKey, savingKey, editedMap) => async (catId) => {
    setSaving(prev => ({ ...prev, [savingKey(catId)]: true }));
    try {
      const value = editedMap[catId];
      await authAxios.put(endpointFor(catId), { [fieldKey]: value === "" ? null : value });
      toast.success("Margine salvato");
      fetchData();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(prev => ({ ...prev, [savingKey(catId)]: false })); }
  };

  const buildSaveAll = (endpointFor, fieldKey, allKey, editedMap, currentValOf) => async () => {
    setSaving({ [allKey]: true });
    try {
      for (const cat of categories) {
        const newVal = editedMap[cat.id];
        if (newVal !== currentValOf(cat)) {
          await authAxios.put(endpointFor(cat.id), { [fieldKey]: newVal === "" ? null : newVal });
        }
      }
      toast.success("Margini salvati");
      fetchData();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving({}); }
  };

  // Markup (Prezzo Minimo)
  const saveSingleMarkup = buildSaveSingle(id => `/categories/${id}/markup`, "markup_percentage", id => id, editedMarkups);
  const saveAllMarkups = buildSaveAll(id => `/categories/${id}/markup`, "markup_percentage", "all", editedMarkups, c => c.markup_percentage);
  const applyMarkupToProducts = async () => {
    setApplying(true); setConfirmDialogOpen(false);
    try { const r = await authAxios.post("/categories/apply-markup"); toast.success(r.data.message); }
    catch { toast.error("Errore nell'applicazione"); }
    finally { setApplying(false); }
  };
  const hasMarkupChanges = categories.some(cat => editedMarkups[cat.id] !== cat.markup_percentage);

  // Auth markup (Prezzo Autorizzare)
  const saveSingleAuthMarkup = buildSaveSingle(id => `/categories/${id}/auth-markup`, "auth_markup_percentage", id => `auth_${id}`, editedAuthMarkups);
  const saveAllAuthMarkups = buildSaveAll(id => `/categories/${id}/auth-markup`, "auth_markup_percentage", "allAuth", editedAuthMarkups, c => c.auth_markup_percentage ?? "");
  const applyAuthMarkupToProducts = async () => {
    setApplyingAuth(true); setConfirmAuthDialogOpen(false);
    try { const r = await authAxios.post("/categories/apply-auth-markup"); toast.success(r.data.message); fetchData(); }
    catch { toast.error("Errore nell'applicazione"); }
    finally { setApplyingAuth(false); }
  };
  const hasAuthMarkupChanges = categories.some(cat => editedAuthMarkups[cat.id] !== (cat.auth_markup_percentage ?? ""));

  // Discount margin (Prezzo Scontato)
  const saveSingleDiscountMargin = buildSaveSingle(id => `/categories/${id}/discount-margin`, "discount_margin_percentage", id => `disc_${id}`, editedDiscountMargins);
  const saveAllDiscountMargins = buildSaveAll(id => `/categories/${id}/discount-margin`, "discount_margin_percentage", "allDiscount", editedDiscountMargins, c => c.discount_margin_percentage ?? "");
  const applyDiscountToProducts = async () => {
    setApplyingDiscount(true); setConfirmDiscountDialogOpen(false);
    try { const r = await authAxios.post("/categories/apply-discount-margin"); toast.success(r.data.message); fetchData(); }
    catch { toast.error("Errore nell'applicazione"); }
    finally { setApplyingDiscount(false); }
  };
  const hasDiscountChanges = categories.some(cat => (editedDiscountMargins[cat.id] ?? "") !== (cat.discount_margin_percentage ?? ""));

  // Promo
  const applyPromoToSelected = async () => {
    if (selectedProducts.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    setSavingPromo(true);
    try {
      const priceValue = promoPrice === "" ? null : parseFloat(promoPrice);
      const r = await authAxios.post("/products/bulk-promo", { product_ids: selectedProducts, price_promo: priceValue });
      toast.success(r.data.message);
      setSelectedProducts([]); setPromoPrice(""); fetchData();
    } catch { toast.error("Errore nell'applicazione promo"); }
    finally { setSavingPromo(false); }
  };

  const removePromoFromSelected = async () => {
    if (selectedProducts.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    setSavingPromo(true);
    try {
      const r = await authAxios.post("/products/bulk-promo", { product_ids: selectedProducts, price_promo: null });
      toast.success("Promo rimossa da " + r.data.updated + " prodotti");
      setSelectedProducts([]); fetchData();
    } catch { toast.error("Errore nella rimozione promo"); }
    finally { setSavingPromo(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Prezzi" />
        <div className="mb-8 flex items-center gap-4">
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Gestione Prezzi</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Imposta margini per categoria e prezzi promo per articolo</p>
          </div>
        </div>

        <Tabs defaultValue="markup" className="space-y-6">
          <TabsList className="bg-[#18181b] border border-white/5 w-full overflow-x-auto justify-start">
            <TabsTrigger value="markup" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black shrink-0">
              <Percent className="w-4 h-4 mr-2" />Prezzo Minimo
            </TabsTrigger>
            <TabsTrigger value="auth" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white shrink-0">
              <Percent className="w-4 h-4 mr-2" />Prezzo Autorizzare
            </TabsTrigger>
            <TabsTrigger value="promo" className="data-[state=active]:bg-red-500 data-[state=active]:text-white shrink-0">
              <Tag className="w-4 h-4 mr-2" />Prezzi Promo
            </TabsTrigger>
            <TabsTrigger value="discount" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black shrink-0">
              <Percent className="w-4 h-4 mr-2" />Prezzo Scontato
            </TabsTrigger>
          </TabsList>

          <TabsContent value="markup" className="space-y-6">
            <MarginTab
              theme="amber"
              formulaTitle="Prezzo Minimo"
              formulaBody={<>= Prezzo Acquisto / (1 - Margine%)<br/><span className="text-amber-200/70 text-xs">Es: Acquisto 1,00 con margine 20% = 1,00 / 0,80 = 1,25 (marginalità del 20%)</span></>}
              visibilityNote="Visibile a: Admin e Agenti"
              categories={categories}
              fieldKey="markup_percentage"
              editedValues={editedMarkups}
              setEditedValues={setEditedMarkups}
              bulkValue={bulkMargin}
              setBulkValue={setBulkMargin}
              saving={saving}
              hasChanges={hasMarkupChanges}
              onSaveSingle={saveSingleMarkup}
              onSaveAll={saveAllMarkups}
              onApplyToProducts={() => setConfirmDialogOpen(true)}
              applying={applying}
              loading={loading}
              saveAllKey="all"
              singleSavingKey={(id) => id}
              bulkInputTestId="bulk-margin-input"
              bulkApplyTestId="bulk-margin-apply-btn"
            />
          </TabsContent>

          <TabsContent value="auth" className="space-y-6">
            <MarginTab
              theme="purple"
              formulaTitle="Prezzo da Autorizzare"
              formulaBody="= Prezzo Acquisto / (1 - Margine%)"
              visibilityNote="Visibile a: Admin e Agenti | Lascia vuoto per disattivare"
              categories={categories}
              fieldKey="auth_markup_percentage"
              editedValues={editedAuthMarkups}
              setEditedValues={setEditedAuthMarkups}
              bulkValue={bulkAuthMargin}
              setBulkValue={setBulkAuthMargin}
              saving={saving}
              hasChanges={hasAuthMarkupChanges}
              onSaveSingle={saveSingleAuthMarkup}
              onSaveAll={saveAllAuthMarkups}
              onApplyToProducts={() => setConfirmAuthDialogOpen(true)}
              applying={applyingAuth}
              loading={loading}
              saveAllKey="allAuth"
              singleSavingKey={(id) => `auth_${id}`}
              bulkInputTestId="bulk-auth-margin-input"
              bulkApplyTestId="bulk-auth-margin-apply-btn"
            />
          </TabsContent>

          <TabsContent value="discount" className="space-y-6">
            <MarginTab
              theme="cyan"
              formulaTitle="Prezzo Scontato"
              formulaBody={<>= Prezzo Acquisto / (1 - Margine%)<br/><span className="text-cyan-200/70 text-xs">Es: Acquisto 1,00 con margine 30% = 1,00 / 0,70 = 1,43</span></>}
              visibilityNote="Visibile a: Clienti | Se presente un prezzo promo, viene mostrato al suo posto"
              categories={categories}
              fieldKey="discount_margin_percentage"
              editedValues={editedDiscountMargins}
              setEditedValues={setEditedDiscountMargins}
              bulkValue={bulkDiscountMargin}
              setBulkValue={setBulkDiscountMargin}
              saving={saving}
              hasChanges={hasDiscountChanges}
              onSaveSingle={saveSingleDiscountMargin}
              onSaveAll={saveAllDiscountMargins}
              onApplyToProducts={() => setConfirmDiscountDialogOpen(true)}
              applying={applyingDiscount}
              loading={loading}
              saveAllKey="allDiscount"
              singleSavingKey={(id) => `disc_${id}`}
              bulkInputTestId="bulk-discount-margin-input"
              bulkApplyTestId="bulk-discount-margin-apply-btn"
            />
          </TabsContent>

          <TabsContent value="promo" className="space-y-6">
            <PromoTab
              products={products}
              categories={categories}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              promoPrice={promoPrice}
              setPromoPrice={setPromoPrice}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              savingPromo={savingPromo}
              onApplyPromo={applyPromoToSelected}
              onRemovePromo={removePromoFromSelected}
            />
          </TabsContent>
        </Tabs>

        <ApplyConfirmDialog
          open={confirmDialogOpen}
          onOpenChange={setConfirmDialogOpen}
          title="Conferma Applicazione Margini"
          description={<>Questa azione ricalcolerà il <strong className="text-amber-400">prezzo minimo</strong> di tutti i prodotti usando la formula: Prezzo Acquisto / (1 - Margine%).</>}
          onConfirm={applyMarkupToProducts}
        />

        <ApplyConfirmDialog
          open={confirmAuthDialogOpen}
          onOpenChange={setConfirmAuthDialogOpen}
          title="Conferma Applicazione Prezzi da Autorizzare"
          description={<>Questa azione ricalcolerà il <strong className="text-purple-400">prezzo da autorizzare</strong> per i prodotti delle categorie con margine impostato.</>}
          confirmClassName="bg-purple-500 hover:bg-purple-600 text-white"
          onConfirm={applyAuthMarkupToProducts}
        />

        <ApplyConfirmDialog
          open={confirmDiscountDialogOpen}
          onOpenChange={setConfirmDiscountDialogOpen}
          title="Conferma Applicazione Prezzi Scontati"
          description={<>Questa azione ricalcolerà il <strong className="text-cyan-400">prezzo scontato</strong> per i prodotti delle categorie con margine impostato, usando la formula: Prezzo Acquisto / (1 - Margine%).</>}
          confirmClassName="bg-cyan-500 hover:bg-cyan-600 text-black"
          onConfirm={applyDiscountToProducts}
        />
      </main>
    </div>
  );
}
